import React, { createContext, useEffect, useState } from 'react';

// third party
import axios from 'axios';
import { Auth0Provider as AuthProvider, useAuth0 } from '@auth0/auth0-react';

// project imports
import Loader from 'ui-component/Loader';
import axiosServices from 'utils/axios';
import { completeLegacyMigration, loadUserContext, saveUserContext } from 'utils/userContextStorage';

// types
import { Auth0ContextType, InitialLoginContextProps } from 'types/auth';

// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  userNotFound: false,
  user: null
};

const domain: string = window.appConfig.auth0Domain;
const clientId: string = window.appConfig.auth0ClientId;
const audience: string = window.appConfig.auth0Audience;

// ==============================|| AUTH0 CONTEXT & PROVIDER ||============================== //

const Auth0Context = createContext<Auth0ContextType | null>(null);

export function Auth0Provider({ children }: { children: React.ReactElement }) {
  return (
    <AuthProvider
      domain={domain}
      clientId={clientId}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: audience
      }}
    >
      <Auth0ContextProvider>{children}</Auth0ContextProvider>
    </AuthProvider>
  );
}

function Auth0ContextProvider({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, user, isLoading, loginWithPopup, logout, getAccessTokenSilently } = useAuth0();

  //console.log('Auth0ContextProvider - isAuthenticated:', isAuthenticated);
  //console.log('Auth0ContextProvider - user:', user);
  //console.log('Auth0ContextProvider - isLoading:', isLoading);

  // State to track the authentication status and user information
  const [state, setState] = useState<InitialLoginContextProps>(initialState);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      setState({ isLoggedIn: false, isInitialized: true, userNotFound: false, user: null });
      return;
    }

    const initUser = async () => {
      try {
        const token = await getAccessTokenSilently();
        localStorage.setItem('serviceToken', token);
        console.log('serviceToken stored in localStorage');

        // A 4xx from /me means the Auth0 session is valid but Weightrax has no user (or no tenant) for
        // this identity. It is accepted as a response rather than an error so the shared interceptor
        // doesn't bounce a 401 to /login — the user is still signed in to Auth0, so that came straight
        // back here and left a blank page.
        // Send this identity's own last tenant, not whatever the previous sign-in on this browser left
        // in the shared tenant key — /me resolves the session's tenant from that header.
        const savedContext = loadUserContext(user?.sub ?? null);
        saveUserContext(user?.sub ?? null, savedContext);

        const response = await axiosServices.get<{ tenantId: number; defaultTenantId: number; userId: number }>('/api/users/me', {
          validateStatus: (status) => status < 500
        });

        if (response.status >= 400 || !response.data?.tenantId) {
          // Nothing tenant-scoped can load for them, so leave no tenant behind for later requests.
          saveUserContext(user?.sub ?? null, { ...savedContext, tenantId: null });
          setState({
            isLoggedIn: true,
            isInitialized: true,
            userNotFound: true,
            user: { id: user?.sub, avatar: user?.picture, email: user?.email, name: user?.name, tier: 'Premium' }
          });
          return;
        }

        const { data } = response;

        // Restore the context this user last worked in — the tenant header has to be right before
        // any tenant-scoped request goes out, which is well before TenantSelectionProvider mounts.
        // /me answers with the tenant it resolved: the saved one when the user still belongs to it,
        // otherwise their default — so a tenant they've lost access to isn't restored.
        const { tenantId } = data;
        const keepSelection = tenantId === savedContext.tenantId;

        saveUserContext(
          user?.sub ?? null,
          keepSelection ? savedContext : { tenantId, companyId: null, companyName: null, siteId: null, siteName: null }
        );
        completeLegacyMigration();

        setState({
          isLoggedIn: true,
          isInitialized: true,
          userNotFound: false,
          user: {
            id: user?.sub,
            avatar: user?.picture,
            email: user?.email,
            name: user?.name,
            tier: 'Premium',
            defaultTenantId: data.defaultTenantId
          }
        });
      } catch {
        // A network or server failure — not evidence that the user is unknown, so the app loads as
        // normal and the failure surfaces on the page that needed the data.
        setState({
          isLoggedIn: true,
          isInitialized: true,
          userNotFound: false,
          user: {
            id: user?.sub,
            avatar: user?.picture,
            email: user?.email,
            name: user?.name,
            tier: 'Premium'
          }
        });
      }
    };

    initUser();
  }, [isAuthenticated, isLoading, user, getAccessTokenSilently]);

  const loginAuth = async (options?: any) => {
    await loginWithPopup(options);
  };

  const logoutAuth = () => {
    logout();
  };

  // Ends the Auth0 session and comes back to the login page, so the user can sign in again —
  // typically with a different account. Only the Auth0 session is ended (not federated), so a
  // Google user isn't also signed out of Google.
  const retryLogin = () => {
    localStorage.removeItem('serviceToken');
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const resetPassword = async (email: string) => {
    await axios.post(`https://${domain}/dbconnections/change_password`, {
      client_id: clientId,
      email,
      connection: 'Username-Password-Authentication'
    });
  };

  const updateProfile = () => {
    // Implement update profile functionality if needed
  };

  if (isLoading || !state.isInitialized) {
    return <Loader />;
  }

  const contextValue: Auth0ContextType = {
    ...state, // Spread the current state (isLoggedIn, isInitialized, user)
    loginAuth,
    logout: logoutAuth,
    retryLogin,
    resetPassword,
    updateProfile
  };

  return <Auth0Context value={contextValue}>{children}</Auth0Context>;
}

export default Auth0Context;
